import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { Eye, EyeOff, AlertCircle, ShieldCheck, Send, Loader2 } from 'lucide-react'
import { useT, useLang } from '@/contexts/language'
import { useAuth } from '@/contexts/auth'
import { apiLogin, apiLoginVerify, ApiRequestError } from '@/lib/api'
import { isLoginOtpRequired } from '@nexbit/types'
import type { LoginResponse } from '@nexbit/types'

const loginSchema = z.object({
  username: z.string().min(1),
  password: z.string().min(1),
})
type LoginForm = z.infer<typeof loginSchema>

export default function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const t = useT()
  const { toggle } = useLang()
  const { login } = useAuth()
  const [showPassword, setShowPassword] = useState(false)
  const [loginError, setLoginError] = useState<string | null>(null)
  /**
   * Set once an admin's password has been accepted and a code is waiting in the
   * Telegram group. The username is kept because finishing the sign-in is bound to it;
   * the password is deliberately not — it has done its job and never needs sending again.
   */
  const [pendingOtp, setPendingOtp] = useState<{ username: string; challengeId: string } | null>(null)
  const [code, setCode] = useState('')
  const [verifying, setVerifying] = useState(false)

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  })

  /** Take a real session and go where this account belongs. */
  async function enter(res: LoginResponse) {
    const { user, level } = await login(res.accessToken)
    const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname
    const defaultPath =
      user.role === 'admin' ? '/admin' : level === 'master' ? '/master' : '/agent'
    navigate(from && from !== '/login' ? from : defaultPath, { replace: true })
  }

  async function onSubmit(values: LoginForm) {
    setLoginError(null)
    try {
      const res = await apiLogin(values)
      if (isLoginOtpRequired(res)) {
        setPendingOtp({ username: values.username, challengeId: res.challengeId })
        setCode('')
        return
      }
      await enter(res)
    } catch (err) {
      setLoginError(err instanceof ApiRequestError ? err.message : t.auth.invalidCredentials)
    }
  }

  async function onVerify(e: React.FormEvent) {
    e.preventDefault()
    if (!pendingOtp) return
    setLoginError(null)
    setVerifying(true)
    try {
      await enter(await apiLoginVerify({ ...pendingOtp, code }))
    } catch (err) {
      setLoginError(err instanceof ApiRequestError ? err.message : t.auth.invalidCredentials)
      // Cleared so a code already known to be wrong is not resubmitted by reflex into
      // the few attempts the challenge has left.
      setCode('')
    } finally {
      setVerifying(false)
    }
  }

  return (
    <main className="min-h-screen flex items-center justify-center bg-background px-4">
      <button
        type="button"
        onClick={toggle}
        className="fixed top-4 right-4 font-mono text-xs text-muted-foreground hover:text-foreground transition-colors"
        aria-label="Toggle language"
      >
        {t.lang.toggle}
      </button>

      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center gap-3 mb-8">
          <div className="h-10 w-10 rounded-[var(--radius)] bg-primary flex items-center justify-center">
            <ShieldCheck className="h-5 w-5 text-primary-foreground" aria-hidden="true" />
          </div>
          <div className="text-center">
            <h1 className="text-xl font-semibold tracking-tight">Lotmatic Admin</h1>
            <p className="text-xs text-muted-foreground mt-1">{t.auth.signInSubtitle}</p>
          </div>
        </div>

        <div className="border border-border rounded-[var(--radius)] bg-card p-6">
          <h2 className="text-sm font-semibold text-muted-foreground mb-5">
            {pendingOtp ? 'ยืนยันการเข้าสู่ระบบ' : t.auth.signIn}
          </h2>

          {pendingOtp ? (
            <form onSubmit={(e) => void onVerify(e)} className="flex flex-col gap-4" noValidate>
              <div className="flex items-start gap-2 rounded-[var(--radius-sm)] border border-border bg-secondary/40 p-3">
                <Send className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                <p className="text-xs leading-relaxed text-muted-foreground">
                  ส่งรหัส 6 หลักเข้ากลุ่มเทเลแกรมของระบบแล้ว — ข้อความจะบอกชื่อผู้เข้าและเวลา
                  <br />
                  ถ้าไม่ใช่คุณเป็นคนเข้า แจ้งทีมทันทีและเปลี่ยนรหัสผ่าน
                </p>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="otp">รหัสยืนยัน 6 หลัก</Label>
                <Input
                  id="otp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="h-11 text-center font-mono text-lg tracking-[0.4em]"
                />
                <p className="text-xs text-muted-foreground">ใช้ได้ 5 นาที · ใช้ได้ครั้งเดียว</p>
              </div>

            {loginError && (
              <div className="flex items-start gap-2 p-3 border border-destructive/40 rounded-[var(--radius-sm)] bg-destructive/10" role="alert">
                <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                <p className="text-sm text-destructive">{loginError}</p>
              </div>
            )}

              <Button type="submit" disabled={code.length !== 6 || verifying} className="w-full font-semibold mt-1 gap-1.5">
                {verifying && <Loader2 className="h-4 w-4 animate-spin" />}
                {verifying ? t.auth.submitting : 'เข้าสู่ระบบ'}
              </Button>
              <button
                type="button"
                onClick={() => { setPendingOtp(null); setCode(''); setLoginError(null) }}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
              >
                ← กลับไปหน้าเข้าสู่ระบบ
              </button>
            </form>
          ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="username">{t.auth.username}</Label>
              <Input id="username" type="text" autoComplete="username" autoFocus placeholder="username" aria-invalid={!!errors.username} {...register('username')} />
              {errors.username && (
                <p className="text-xs text-destructive flex items-center gap-1" role="alert">
                  <AlertCircle className="h-3 w-3 shrink-0" />{t.auth.usernameRequired}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="password">{t.auth.password}</Label>
              <div className="relative">
                <Input id="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" placeholder="••••••••" className="pr-9" aria-invalid={!!errors.password} {...register('password')} />
                <button
                  type="button"
                  className={cn('absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm')}
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? t.auth.hidePassword : t.auth.showPassword}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && (
                <p className="text-xs text-destructive flex items-center gap-1" role="alert">
                  <AlertCircle className="h-3 w-3 shrink-0" />{t.auth.passwordRequired}
                </p>
              )}
            </div>

            {loginError && (
              <div className="flex items-start gap-2 p-3 border border-destructive/40 rounded-[var(--radius-sm)] bg-destructive/10" role="alert">
                <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                <p className="text-sm text-destructive">{loginError}</p>
              </div>
            )}

            <Button type="submit" disabled={isSubmitting} className="w-full font-semibold mt-1">
              {isSubmitting ? t.auth.submitting : t.auth.signIn}
            </Button>
          </form>
          )}
        </div>

        <p className="text-center text-xs text-muted-foreground/50 mt-6">{t.auth.adminOnly}</p>
      </div>
    </main>
  )
}
